import { useState, useMemo, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { adminApi } from '@/lib/api'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utils'
import {
  Paintbrush, Plus, CheckCircle, XCircle, Pencil, Trash2, X,
  ChevronUp, ChevronDown, ChevronLeft, ChevronRight, FolderOpen,
} from 'lucide-react'
import type { ThemePresetSummary, PlanSlug } from '@/types/admin'

const TIER_LABEL: Record<PlanSlug, string> = {
  basic:      'Basic',
  pro:        'Pro',
  enterprise: 'Enterprise',
}

// Extended type — palette_group, mode & min_plan_slug added in migration 043+
type EnrichedPreset = ThemePresetSummary & {
  palette_group: string | null
  mode: 'dark' | 'light' | 'any'
  min_plan_slug: PlanSlug | null
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
      )}
    >
      {isActive ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
      {isActive ? 'Aktif' : 'Nonaktif'}
    </span>
  )
}

function ColorSwatch({ preset }: { preset: ThemePresetSummary }) {
  const colors = [
    preset.color_primary, preset.color_secondary, preset.color_accent,
    preset.color_background, preset.color_surface,
    preset.color_text_primary, preset.color_text_muted, preset.color_border,
  ]
  return (
    <div className="flex gap-0.5 h-5 rounded overflow-hidden w-28 border border-border">
      {colors.map((color, i) => (
        <div key={i} style={{ background: color, flex: 1 }} title={color} />
      ))}
    </div>
  )
}

// ── Rename Group Dialog ───────────────────────────────────────────────────────

function RenameGroupDialog({
  groupName,
  presetCount,
  onSave,
  onClose,
  isSaving,
  error,
}: {
  groupName: string
  presetCount: number
  onSave: (newName: string) => void
  onClose: () => void
  isSaving: boolean
  error?: string | null
}) {
  const [value, setValue] = useState(groupName)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = value.trim()
    if (!trimmed || trimmed === groupName) { onClose(); return }
    onSave(trimmed)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" />
      <div
        className="relative z-10 w-full max-w-sm rounded-xl border border-border bg-card shadow-xl p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-muted"
        >
          <X className="h-4 w-4" />
        </button>

        <div>
          <h2 className="font-semibold text-base">Ganti Nama Grup</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Nama baru akan diterapkan ke semua {presetCount} preset dalam grup ini.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Nama grup"
            disabled={isSaving}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50"
          />

          {error && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="flex-1 rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors disabled:opacity-50"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={isSaving || !value.trim() || value.trim() === groupName}
              className="flex-1 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {isSaving ? 'Menyimpan…' : 'Simpan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Folder card (root view) ───────────────────────────────────────────────────

function FolderCard({
  name,
  presets,
  onClick,
  onRename,
}: {
  name: string
  presets: EnrichedPreset[]
  onClick: () => void
  onRename: () => void
}) {
  const previews = [
    ...presets.filter((p) => p.mode === 'dark'),
    ...presets.filter((p) => p.mode !== 'dark'),
  ].slice(0, 4)

  const darkCount  = presets.filter((p) => p.mode === 'dark').length
  const lightCount = presets.filter((p) => p.mode === 'light').length

  return (
    <div className="relative group">
      <button
        type="button"
        onClick={onClick}
        className="rounded-xl border border-border bg-card overflow-hidden text-left hover:border-primary/60 hover:shadow-md transition-all w-full"
      >
        {/* Color strip previews */}
        <div className="h-20 bg-muted/50 p-2 flex flex-col gap-1 overflow-hidden">
          {previews.map((preset, i) => (
            <div key={i} className="flex gap-0.5 flex-1 rounded overflow-hidden">
              <div style={{ background: preset.color_background, flex: 3 }} />
              <div style={{ background: preset.color_surface,    flex: 1 }} />
              <div style={{ background: preset.color_primary,    flex: 1 }} />
              <div style={{ background: preset.color_accent,     flex: 1 }} />
              <div style={{ background: preset.color_border,     flex: 1 }} />
            </div>
          ))}
          {previews.length === 0 && (
            <div className="flex-1 flex items-center justify-center">
              <FolderOpen className="h-6 w-6 text-muted-foreground/20" />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-3 py-2.5 flex items-center gap-2">
          <FolderOpen className="h-3.5 w-3.5 text-primary shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-xs truncate">{name}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              {darkCount > 0 && `${darkCount} dark`}
              {darkCount > 0 && lightCount > 0 && ' · '}
              {lightCount > 0 && `${lightCount} light`}
              {darkCount === 0 && lightCount === 0 && `${presets.length} preset`}
            </p>
          </div>
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground transition-colors shrink-0" />
        </div>
      </button>

      {/* Rename button — appears on hover */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRename() }}
        title="Ganti nama grup"
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 rounded-md p-1 bg-background/80 text-muted-foreground hover:text-foreground transition-all"
      >
        <Pencil className="h-3 w-3" />
      </button>
    </div>
  )
}

// ── Preset table ──────────────────────────────────────────────────────────────

function PresetTable({
  presets,
  deletingId,
  toggleActive,
  moveOrder,
  onDelete,
}: {
  presets: EnrichedPreset[]
  deletingId: string | null
  toggleActive: { mutate: (args: { id: string; is_active: boolean }) => void; isPending: boolean }
  moveOrder:    { mutate: (args: { id: string; sort_order: number }) => void; isPending: boolean }
  onDelete: (preset: EnrichedPreset) => void
}) {
  if (presets.length === 0) {
    return (
      <div className="rounded-xl border border-border p-8 text-center text-sm text-muted-foreground">
        Belum ada preset untuk mode ini.
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-border overflow-hidden">
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-muted/40">
          <tr>
            <th className="text-left px-4 py-3 font-medium text-muted-foreground">Nama</th>
            <th className="text-left px-4 py-3 font-medium text-muted-foreground">Palet Warna</th>
            <th className="text-left px-4 py-3 font-medium text-muted-foreground">Font</th>
            <th className="text-left px-4 py-3 font-medium text-muted-foreground">Tier</th>
            <th className="text-left px-4 py-3 font-medium text-muted-foreground">Status</th>
            <th className="text-left px-4 py-3 font-medium text-muted-foreground w-28">Urutan</th>
            <th className="text-right px-4 py-3 font-medium text-muted-foreground">Aksi</th>
          </tr>
        </thead>
        <tbody>
          {presets.map((preset, idx) => (
            <tr key={preset.id} className="border-b border-border last:border-0 hover:bg-muted/20">
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  {preset.thumbnail_url ? (
                    <img
                      src={preset.thumbnail_url}
                      alt=""
                      className="h-8 w-12 rounded object-cover border border-border"
                    />
                  ) : (
                    <div className="h-8 w-12 rounded border border-border bg-muted flex items-center justify-center">
                      <Paintbrush className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                  )}
                  <div>
                    <p className="font-medium">{preset.name}</p>
                    {preset.description && (
                      <p className="text-xs text-muted-foreground line-clamp-1 max-w-48">
                        {preset.description}
                      </p>
                    )}
                    {preset.mode !== 'any' && (
                      <span className={cn(
                        'inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded-full font-medium',
                        preset.mode === 'dark'
                          ? 'bg-slate-800 text-slate-200'
                          : 'bg-amber-50 text-amber-700 border border-amber-200'
                      )}>
                        {preset.mode}
                      </span>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-4 py-3">
                <ColorSwatch preset={preset} />
              </td>
              <td className="px-4 py-3 text-muted-foreground text-xs">
                <span>{preset.font_family_heading}</span>
                {preset.font_family_heading !== preset.font_family_body && (
                  <span className="text-muted-foreground/50"> / {preset.font_family_body}</span>
                )}
              </td>
              <td className="px-4 py-3">
                {preset.min_plan_slug ? (
                  <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                    {TIER_LABEL[preset.min_plan_slug]}+
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground/50">Semua</span>
                )}
              </td>
              <td className="px-4 py-3">
                <button
                  onClick={() => toggleActive.mutate({ id: preset.id, is_active: !preset.is_active })}
                  disabled={toggleActive.isPending}
                  className="cursor-pointer disabled:opacity-50"
                >
                  <StatusBadge isActive={preset.is_active} />
                </button>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1">
                  <span className="w-8 text-xs text-muted-foreground tabular-nums">{preset.sort_order}</span>
                  <div className="flex flex-col">
                    <button
                      onClick={() => moveOrder.mutate({ id: preset.id, sort_order: preset.sort_order - 1 })}
                      disabled={idx === 0 || moveOrder.isPending}
                      className="rounded p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-20"
                    >
                      <ChevronUp className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => moveOrder.mutate({ id: preset.id, sort_order: preset.sort_order + 1 })}
                      disabled={idx === presets.length - 1 || moveOrder.isPending}
                      className="rounded p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-20"
                    >
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-2">
                  <Link
                    to={`/theme-presets/${preset.id}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-foreground hover:border-primary hover:text-primary transition-colors"
                  >
                    <Pencil className="h-3 w-3" />
                    Edit
                  </Link>
                  <button
                    onClick={() => onDelete(preset)}
                    disabled={false}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors disabled:opacity-50',
                      deletingId === preset.id
                        ? 'border-destructive bg-destructive/10 text-destructive'
                        : 'border-border text-muted-foreground hover:border-destructive hover:text-destructive'
                    )}
                    title={deletingId === preset.id ? 'Klik lagi untuk konfirmasi' : 'Hapus'}
                  >
                    <Trash2 className="h-3 w-3" />
                    {deletingId === preset.id ? 'Konfirmasi?' : 'Hapus'}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ThemePresetsPage() {
  const qc = useQueryClient()
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null)
  const [activeMode, setActiveMode] = useState<'dark' | 'light'>('dark')
  const [renamingGroup, setRenamingGroup] = useState<string | null>(null)

  // Primary: preset list from admin API
  const { data: rawPresets = [], isLoading } = useQuery({
    queryKey: ['admin', 'theme-presets'],
    queryFn: () => adminApi.themePresets.list(),
  })

  // Secondary: palette_group, mode & min_plan_slug from Supabase (not yet in edge API)
  const { data: groupMeta = [] } = useQuery({
    queryKey: ['admin', 'theme-presets-grouping'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('theme_presets')
        .select('id, palette_group, mode, min_plan_slug')
      if (error) throw error
      return data as { id: string; palette_group: string | null; mode: string; min_plan_slug: string | null }[]
    },
    staleTime: 60_000,
  })

  // Merge grouping metadata into preset list
  const presets = useMemo<EnrichedPreset[]>(() => {
    const lookup = new Map(groupMeta.map((g) => [g.id, g]))
    return rawPresets.map((p) => ({
      ...p,
      palette_group: lookup.get(p.id)?.palette_group ?? null,
      mode: (lookup.get(p.id)?.mode ?? 'any') as 'dark' | 'light' | 'any',
      min_plan_slug: (lookup.get(p.id)?.min_plan_slug ?? null) as PlanSlug | null,
    }))
  }, [rawPresets, groupMeta])

  // Split into ungrouped flat list + named groups map
  const { ungrouped, groups } = useMemo(() => {
    const ungrouped: EnrichedPreset[] = []
    const groupMap = new Map<string, EnrichedPreset[]>()
    for (const p of presets) {
      if (!p.palette_group) {
        ungrouped.push(p)
      } else {
        if (!groupMap.has(p.palette_group)) groupMap.set(p.palette_group, [])
        groupMap.get(p.palette_group)!.push(p)
      }
    }
    return { ungrouped, groups: groupMap }
  }, [presets])

  const toggleActive = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      adminApi.themePresets.update(id, { is_active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'theme-presets'] }),
  })

  const moveOrder = useMutation({
    mutationFn: ({ id, sort_order }: { id: string; sort_order: number }) =>
      adminApi.themePresets.update(id, { sort_order }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'theme-presets'] }),
  })

  const deletePreset = useMutation({
    mutationFn: (id: string) => adminApi.themePresets.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'theme-presets'] })
      setDeletingId(null)
    },
  })

  const renameGroup = useMutation({
    mutationFn: async ({ oldName, newName }: { oldName: string; newName: string }) => {
      const { error } = await supabase
        .from('theme_presets')
        .update({ palette_group: newName })
        .eq('palette_group', oldName)
      if (error) throw error
    },
    onSuccess: (_, { oldName, newName }) => {
      qc.invalidateQueries({ queryKey: ['admin', 'theme-presets-grouping'] })
      if (selectedGroup === oldName) setSelectedGroup(newName)
      setRenamingGroup(null)
    },
  })

  function handleDelete(preset: EnrichedPreset) {
    if (deletingId === preset.id) {
      deletePreset.mutate(preset.id)
    } else {
      setDeletingId(preset.id)
      setTimeout(() => setDeletingId(null), 3000)
    }
  }

  // Current folder presets (null = root view)
  const folderPresets = selectedGroup ? (groups.get(selectedGroup) ?? []) : null
  const folderModes   = folderPresets
    ? new Set(folderPresets.map((p) => p.mode).filter((m) => m !== 'any'))
    : null
  const showModeFilter = folderModes?.has('dark') && folderModes?.has('light')
  const visibleInFolder = folderPresets
    ? (showModeFilter
        ? folderPresets.filter((p) => p.mode === activeMode || p.mode === 'any')
        : folderPresets)
    : []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          {selectedGroup ? (
            <>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <button
                  type="button"
                  onClick={() => setSelectedGroup(null)}
                  className="hover:text-foreground transition-colors flex items-center gap-1"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Tema Preset
                </button>
                <span>/</span>
                <div className="flex items-center gap-1.5 text-foreground font-medium">
                  <FolderOpen className="h-3.5 w-3.5 text-primary" />
                  {selectedGroup}
                  <button
                    type="button"
                    onClick={() => setRenamingGroup(selectedGroup)}
                    title="Ganti nama grup"
                    className="rounded p-0.5 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                </div>
                <span className="text-muted-foreground">
                  ({folderPresets?.length ?? 0} preset)
                </span>
              </div>
              <h1 className="text-2xl font-bold">{selectedGroup}</h1>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold">Tema Preset</h1>
              <p className="text-sm text-muted-foreground">
                Kelola paket tema siap pakai yang bisa diterapkan tenant dengan satu klik.
              </p>
            </>
          )}
        </div>
        <Link
          to="/theme-presets/create"
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          Buat Tema
        </Link>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="rounded-xl border border-border p-8 text-center text-sm text-muted-foreground">
          Memuat...
        </div>
      )}

      {/* Empty state (root, no presets at all) */}
      {!isLoading && presets.length === 0 && (
        <div className="rounded-xl border border-border p-8 text-center">
          <Paintbrush className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Belum ada tema preset.</p>
          <Link
            to="/theme-presets/create"
            className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
          >
            <Plus className="h-3.5 w-3.5" />
            Buat tema pertama
          </Link>
        </div>
      )}

      {/* ── Inside folder view ── */}
      {!isLoading && selectedGroup !== null && (
        <div className="space-y-4">
          {/* Mode filter */}
          {showModeFilter && (
            <div className="flex gap-1 rounded-lg border border-border bg-muted/40 p-1 w-fit">
              {(['dark', 'light'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setActiveMode(m)}
                  className={cn(
                    'rounded-md px-4 py-1.5 text-sm font-medium transition-colors',
                    activeMode === m
                      ? 'bg-card shadow-sm text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {m === 'dark' ? 'Dark Mode' : 'Light Mode'}
                </button>
              ))}
            </div>
          )}

          <PresetTable
            presets={visibleInFolder}
            deletingId={deletingId}
            toggleActive={toggleActive}
            moveOrder={moveOrder}
            onDelete={handleDelete}
          />

          {!isLoading && visibleInFolder.length > 0 && (
            <p className="text-xs text-muted-foreground">{visibleInFolder.length} preset</p>
          )}
        </div>
      )}

      {/* ── Root view: folder cards + ungrouped table ── */}
      {!isLoading && selectedGroup === null && presets.length > 0 && (
        <div className="space-y-8">
          {/* Palette group folders */}
          {groups.size > 0 && (
            <div className="space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Koleksi Palet ({groups.size} grup · {presets.length - ungrouped.length} preset)
              </p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {[...groups.entries()].map(([groupName, groupPresets]) => (
                  <FolderCard
                    key={groupName}
                    name={groupName}
                    presets={groupPresets}
                    onClick={() => {
                      setSelectedGroup(groupName)
                      const modes = new Set(groupPresets.map((p) => p.mode).filter((m) => m !== 'any'))
                      setActiveMode(modes.has('dark') ? 'dark' : 'light')
                    }}
                    onRename={() => setRenamingGroup(groupName)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Ungrouped presets */}
          {ungrouped.length > 0 && (
            <div className="space-y-3">
              {groups.size > 0 && (
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Preset Lainnya
                </p>
              )}
              <PresetTable
                presets={ungrouped}
                deletingId={deletingId}
                toggleActive={toggleActive}
                moveOrder={moveOrder}
                onDelete={handleDelete}
              />
              <p className="text-xs text-muted-foreground">{ungrouped.length} preset</p>
            </div>
          )}
        </div>
      )}

      {/* Rename group dialog */}
      {renamingGroup !== null && (
        <RenameGroupDialog
          groupName={renamingGroup}
          presetCount={groups.get(renamingGroup)?.length ?? 0}
          onSave={(newName) => renameGroup.mutate({ oldName: renamingGroup, newName })}
          onClose={() => { setRenamingGroup(null); renameGroup.reset() }}
          isSaving={renameGroup.isPending}
          error={renameGroup.error?.message ?? null}
        />
      )}
    </div>
  )
}
